import React, { useEffect, useState, useCallback } from "react";
import { Plus, Package, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { useBrand } from "@/context/BrandContext";
import { formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white transition-colors";

export default function Products() {
  const { selectedBrand, brands } = useBrand();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand: "", name: "", sku: "", category: "", mrp: "", price: "", cost: "", variants: [] });

  const load = useCallback(() => api.get("/products", { params: { brand: selectedBrand } }).then((r) => setProducts(r.data)), [selectedBrand]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.brand || !form.name) return toast.error("Brand and name required");
    try {
      await api.post("/products", { ...form, mrp: Number(form.mrp || 0), price: Number(form.price || 0), cost: Number(form.cost || 0) });
      toast.success("Product added"); setOpen(false); setForm({ brand: "", name: "", sku: "", category: "", mrp: "", price: "", cost: "", variants: [] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/products/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Master Product Catalogue" subtitle={`${products.length} products · ${selectedBrand}`}
        actions={<button data-testid="add-product-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors"><Plus size={16} /> New Product</button>} />
      <div className="p-8">
        {products.length === 0 ? <EmptyState icon={Package} title="No products" hint="Add products to the brand catalogue." /> : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-white/10 bg-[#0a0a0a]">{["Product", "SKU", "Brand", "Category", "MRP", "Price", "Variants", ""].map((h) => <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>)}</tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                    <td className="py-3 px-3 text-zinc-200">{p.name}</td>
                    <td className="py-3 px-3 font-mono text-xs text-zinc-400">{p.sku}</td>
                    <td className="py-3 px-3 text-zinc-400">{p.brand}</td>
                    <td className="py-3 px-3 text-zinc-400">{p.category}</td>
                    <td className="py-3 px-3 font-mono text-zinc-400">{formatINR(p.mrp)}</td>
                    <td className="py-3 px-3 font-mono text-zinc-200">{formatINR(p.price)}</td>
                    <td className="py-3 px-3">{(p.variants || []).length ? <div className="flex flex-wrap gap-1">{p.variants.map((v) => <Chip key={v.sku} className="bg-white/5 border-white/15 text-zinc-400">{v.name}</Chip>)}</div> : <span className="text-zinc-600 text-xs">—</span>}</td>
                    <td className="py-3 px-3"><button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Product</DialogTitle><DialogDescription className="sr-only">Add a product to the brand master catalogue.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="product-brand-select" className={inputCls} value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}><option value="">Brand *</option>{brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}</select>
              <input className={inputCls} placeholder="SKU" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
            </div>
            <input data-testid="product-name-input" className={inputCls} placeholder="Product name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <div className="grid grid-cols-4 gap-3">
              <input className={inputCls} placeholder="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} />
              <input type="number" className={inputCls} placeholder="MRP" value={form.mrp} onChange={(e) => setForm((f) => ({ ...f, mrp: e.target.value }))} />
              <input type="number" className={inputCls} placeholder="Price" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
              <input type="number" className={inputCls} placeholder="Cost" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} />
            </div>
            <button data-testid="product-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors">Add Product</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
